import { useMemo, useState } from 'react';

import { COMMON_UI_UTILS, MarketId } from '@drift/common';
import { SpotMarketConfig } from '@drift-labs/sdk';

import useModal from 'src/components/Modals/hooks/useModal';
import useAccountData from 'src/hooks/useAccountData';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import { useCancelInsuranceFundUnstake } from 'src/hooks/driftActions/insuranceFund/useCancelInsuranceFundUnstake';
import { useWithdrawInsuranceFundAssets } from 'src/hooks/driftActions/insuranceFund/useWithdrawInsuranceFundAssets';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useUtilizationIsTooHighForIFUnstake from 'src/hooks/useUtilizationIsTooHighForIFUnstake';
import { IfData } from 'src/utils/insuranceFund';
import { useTriggerRefreshIfStakeData } from 'src/hooks/query/ifData/useAllIfData';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

const useActionButtons = ({
	ifData,
	spotMarket,
}: {
	ifData: IfData | undefined;
	spotMarket: SpotMarketConfig;
}) => {
	const { connected } = useCurrentWalletAdapter();
	const accountIsDelegate = useAccountData()?.isDelegatedTo;
	const [submitting, setSubmitting] = useState(false);
	const { cancelInsuranceFundUnstakeRequest } = useCancelInsuranceFundUnstake();
	const { withdrawInsuranceFundUnstakedAssets } =
		useWithdrawInsuranceFundAssets();
	const { show } = useModal();
	const { captureEvent } = usePostHogCapture();
	const refetch = useTriggerRefreshIfStakeData();
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const depositPaused =
		ifData?.pausedOperations.includes(
			COMMON_UI_UTILS.InsuranceFundOperationsMap.ADD
		) ?? true;
	const requestWithdrawPaused =
		ifData?.pausedOperations.includes(
			COMMON_UI_UTILS.InsuranceFundOperationsMap.REQUEST_REMOVE
		) ?? true;
	const withdrawPaused =
		ifData?.pausedOperations.includes(
			COMMON_UI_UTILS.InsuranceFundOperationsMap.REMOVE
		) ?? true;

	const selectedMarketId: MarketId = useMemo(
		() => MarketId.createSpotMarket(spotMarket.marketIndex),
		[spotMarket.marketIndex]
	);

	const unstakeUtilizationCutoffResult =
		useUtilizationIsTooHighForIFUnstake(selectedMarketId);

	const unstakeRequestTimestamp = ifData?.userStake?.unstakeRequest?.timestamp; // in ms
	const unstakePeriod =
		(ifData?.vault.account?.unstakingPeriod.toNumber() ?? 0) * 1000; // in ms

	const unstakeInProgress = !!unstakeRequestTimestamp;

	const unstakeTimeoutExpired =
		unstakeInProgress &&
		Date.now() >= (unstakeRequestTimestamp ?? 0) + unstakePeriod;

	const canWithdraw =
		unstakeInProgress &&
		!unstakeUtilizationCutoffResult.unstakingIsDisabled &&
		unstakeTimeoutExpired;

	const currentStake = ifData?.userStake?.currentStake ?? 0;

	const isStakeDisabled =
		accountIsDelegate || depositPaused || isPrivyNativeAuth;

	const handleClickStakeButton = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();

		if (!accountIsDelegate) {
			show({
				type: 'stake',
				props: {
					selectedIfStakeMarket: spotMarket,
				},
			});
		}
	};

	const handleCancelUnstakeRequest = async () => {
		if (accountIsDelegate) return;
		setSubmitting(true);
		try {
			await cancelInsuranceFundUnstakeRequest({
				marketIndex: spotMarket.marketIndex,
			});
			setTimeout(() => {
				refetch();
			}, 1000);
			captureEvent('canceled_unstake_request', {
				spot_market_symbol: spotMarket.symbol,
			});
		} finally {
			setSubmitting(false);
		}
	};

	const handleWithdrawUnstakedAssets = async () => {
		if (accountIsDelegate) return;
		setSubmitting(true);
		try {
			await withdrawInsuranceFundUnstakedAssets({
				marketIndex: spotMarket.marketIndex,
			});
			setTimeout(() => refetch(), 1000);
			captureEvent('withdrew_unstaked_assets', {
				spot_market_symbol: spotMarket.symbol,
			});
		} finally {
			setSubmitting(false);
		}
	};

	const handleClickUnstakeButton = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();

		if (!accountIsDelegate) {
			if (unstakeInProgress) {
				if (canWithdraw) {
					handleWithdrawUnstakedAssets();
				} else {
					handleCancelUnstakeRequest();
				}
			} else {
				show({
					type: 'stake',
					props: {
						selectedIfStakeMarket: spotMarket,
						initialTab: 'unstake',
					},
				});
			}
		}
	};

	return {
		connected,
		accountIsDelegate,
		submitting,
		isStakeDisabled,
		requestWithdrawPaused,
		withdrawPaused,
		unstakeInProgress,
		canWithdraw,
		currentStake,
		handleClickStakeButton,
		handleClickUnstakeButton,
	};
};

export default useActionButtons;
